import Papa from 'papaparse'
import type {
  CsvColumn,
  CsvRecord,
  FileMeta,
  ProblemRow,
  UploadAnalysis,
  ValidationSummary,
  VendorRouting,
} from './types'
import { SALESFORCE_FIELDS, VENDOR_ROUTING } from './mockData'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export async function parseCsvUpload(file: File): Promise<UploadAnalysis> {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    throw new Error('Choose a CSV file (.csv). Spreadsheet files such as .xlsx are not supported yet.')
  }
  if (file.size === 0) throw new Error('This CSV is empty. Choose a file with a header row and at least one lead.')
  if (file.size > 25 * 1024 * 1024) throw new Error('This CSV is larger than 25 MB. Choose a smaller file.')

  const result = await new Promise<Papa.ParseResult<Record<string, unknown>>>((resolve, reject) => {
    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      complete: resolve,
      error: (error) => reject(new Error(`We couldn't read this CSV: ${error.message}`)),
    })
  })

  const headers = (result.meta.fields ?? []).map((header) => header.trim()).filter(Boolean)
  if (headers.length === 0 || result.data.length === 0) {
    throw new Error('This CSV needs a header row and at least one data row.')
  }
  if (looksHeaderless(headers)) {
    throw new Error('This file appears to be missing column headers. Add a header row, then try again.')
  }
  if (result.errors.some((error) => error.type === 'Quotes' || error.type === 'Delimiter')) {
    throw new Error('We found malformed quoted values in this CSV. Fix the file and try again.')
  }

  const records: CsvRecord[] = result.data.map((source, index) => ({
    row: index + 2,
    values: Object.fromEntries(headers.map((header) => [header, toText(source[header])])),
  }))
  const columns = createColumns(headers, records)
  const validation = validateUpload(records, columns)
  const fileMeta: FileMeta = {
    name: file.name,
    sizeLabel: formatFileSize(file.size),
    rows: records.length,
    columns: headers.length,
    uploadedAt: new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date()),
    validation: validation.errorRows > 0 ? 'error' : validation.warningRows > 0 ? 'warning' : 'valid',
  }

  return { file: fileMeta, columns, records, problems: validation.problems, summary: validation, routing: deriveVendorRouting(records, columns) }
}

export function validateUpload(records: CsvRecord[], columns: CsvColumn[]): ValidationSummary {
  const headerForField = new Map<string, string>()
  columns.forEach((column) => {
    if (column.mappedTo && column.status !== 'ignored' && !headerForField.has(column.mappedTo)) {
      headerForField.set(column.mappedTo, column.header)
    }
  })
  const problems: ProblemRow[] = []
  const seen = new Map<string, number>()

  records.forEach((record) => {
    const valueFor = (fieldId: string) => record.values[headerForField.get(fieldId) ?? '']?.trim() ?? ''
    const name = [valueFor('firstName'), valueFor('lastName')].filter(Boolean).join(' ') || `Row ${record.row}`
    const email = valueFor('email')
    const phone = valueFor('phone')
    const vendor = valueFor('vendor') || '—'

    SALESFORCE_FIELDS.filter((field) => field.required).forEach((field) => {
      if (headerForField.has(field.id) && !valueFor(field.id)) {
        problems.push(problem(record.row, name, phone, email, vendor, `Missing ${field.label}`, 'error', `Add a ${field.label.toLowerCase()} value.`))
      }
    })
    if (email && !EMAIL_PATTERN.test(email)) {
      problems.push(problem(record.row, name, phone, email, vendor, 'Invalid email format', 'error', 'Use a complete address, such as name@example.com.'))
    }
    const phoneDigits = phone.replace(/\D/g, '')
    if (phone && (phoneDigits.length < 10 || phoneDigits.length > 15)) {
      problems.push(problem(record.row, name, phone, email, vendor, 'Invalid phone number', 'error', 'Use a 10–15 digit phone number.'))
    }

    const duplicateKey = email ? `email:${email.toLowerCase()}` : phoneDigits ? `phone:${phoneDigits}` : ''
    if (duplicateKey) {
      const originalRow = seen.get(duplicateKey)
      if (originalRow) {
        problems.push(problem(record.row, name, phone, email, vendor, `Duplicate of row ${originalRow}`, 'warning', 'Review or remove the duplicate lead.'))
      } else {
        seen.set(duplicateKey, record.row)
      }
    }
  })

  const errorRows = new Set(problems.filter((item) => item.severity === 'error').map((item) => item.row))
  const warningRows = new Set(problems.filter((item) => item.severity === 'warning').map((item) => item.row))
  return {
    problems,
    errorRows: errorRows.size,
    warningRows: warningRows.size,
    validRows: records.length - errorRows.size,
  }
}

export function deriveVendorRouting(records: CsvRecord[], columns: CsvColumn[], vendorOverride?: string | null): VendorRouting[] {
  const vendorColumn = columns.find((column) => column.mappedTo === 'vendor' && column.status !== 'ignored')
  const grouped = new Map<string, { label: string; count: number }>()
  records.forEach((record) => {
    const label = vendorOverride
      ? vendorOverride
      : vendorColumn ? record.values[vendorColumn.header]?.trim() || 'Unspecified vendor' : 'Vendor not mapped'
    const key = normalize(label)
    const current = grouped.get(key)
    grouped.set(key, { label: current?.label ?? label, count: (current?.count ?? 0) + 1 })
  })

  return Array.from(grouped.entries()).map(([key, group]) => {
    const recommendation = VENDOR_ROUTING.find((item) => normalize(item.vendor) === key)
    return {
      vendor: group.label,
      rowCount: group.count,
      teliCampaignId: recommendation?.teliCampaignId ?? null,
      sendgridCampaignId: recommendation?.sendgridCampaignId ?? null,
      five9ListId: recommendation?.five9ListId ?? null,
      autoSelected: Boolean(recommendation),
    }
  })
}

function createColumns(headers: string[], records: CsvRecord[]): CsvColumn[] {
  return headers.map((header) => {
    const normalizedHeader = normalize(header)
    const suggested = SALESFORCE_FIELDS.find((field) => field.aliases.some((alias) => normalize(alias) === normalizedHeader) || normalize(field.label) === normalizedHeader)
    return {
      header,
      sample: records.find((record) => record.values[header])?.values[header] ?? '—',
      mappedTo: suggested?.id ?? null,
      status: suggested ? 'mapped' : 'unsupported',
    }
  })
}

function problem(row: number, name: string, phone: string, email: string, vendor: string, issue: string, severity: 'warning' | 'error', suggestion: string): ProblemRow {
  return { row, name, phone, email, vendor, issue, severity, suggestion }
}

function looksHeaderless(headers: string[]): boolean {
  if (headers.length < 2) return true
  return headers.every((header) => /@|^\d{3}[\s().-]?\d{3}|^\$?\d+[\d,.]*$/.test(header.trim()))
}

function normalize(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '')
}

function toText(value: unknown): string {
  return value == null ? '' : String(value).trim()
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}
